import PrettyNumberTextField from "../../common/PrettyNumberTextField";
import { FilterChip } from "../components/FilterChip";
import * as styles from "../styles";
import {
  FILTER_VALUES_LOADER_SIZE_PX,
  NUMERIC_FILTER_HISTOGRAM_HEIGHT_PX,
  NUMERIC_FILTER_INPUT_WIDTH,
  NUMERIC_FILTER_MIN_BAR_HEIGHT_PX,
  NUMERIC_FILTER_SLIDER_RELEASE_DELAY_MS,
} from "../types/constants";
import {
  affixesForValue,
  createNumberFormatter,
  subTypeValueAffixes,
} from "../utils/numberUtils";
import { Box, Button, MenuItem, Slider, Typography } from "@mui/material";
import DefaultLoader from "components/common/DefaultLoader";
import * as labels from "config.labels";
import React from "react";
import { useMemo } from "react";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const NumericFilter = ({
  minMaxValues,
  pendingRange,
  onNumberRangeChange,
  histogramBins,
  sliderDraggingRef,
  showActions = false,
  onSave,
  onCancel,
  dataType,
  valueSubType = null,
  blankCount,
  blankSelected,
  onToggleBlank,
  loading = false,
}) => {
  const translateLabels = staticDataHooks.useTranslator("uiLabels");
  const affixes = useMemo(() => subTypeValueAffixes(valueSubType), [
    valueSubType,
  ]);
  const fmt = useMemo(() => createNumberFormatter(dataType, 1, affixes), [
    dataType,
    affixes,
  ]);
  const fallbackMin = Math.floor(minMaxValues?.min ?? 0);
  const fallbackMax = Math.ceil(minMaxValues?.max ?? 100);
  const pendingMin = pendingRange?.min;
  const pendingMax = pendingRange?.max;
  const minAffixes = affixesForValue(pendingMin, affixes);
  const maxAffixes = affixesForValue(pendingMax, affixes);
  const sliderValueMin =
    pendingMin !== undefined && pendingMin !== null
      ? Math.floor(pendingMin)
      : fallbackMin;
  const sliderValueMax =
    pendingMax !== undefined && pendingMax !== null
      ? Math.ceil(pendingMax)
      : fallbackMax;

  const maxBinCount = useMemo(() => {
    if (!histogramBins || histogramBins.length === 0) return 0;
    return Math.max(...histogramBins);
  }, [histogramBins]);
  const graphHeight = NUMERIC_FILTER_HISTOGRAM_HEIGHT_PX;

  const handleMinChange = (num) => {
    if (blankSelected && num != null) {
      onToggleBlank();
    }
    onNumberRangeChange({
      min: num,
      max: pendingRange?.max ?? null,
    });
  };

  const handleMaxChange = (num) => {
    if (blankSelected && num != null) {
      onToggleBlank();
    }
    onNumberRangeChange({
      min: pendingRange?.min ?? null,
      max: num,
    });
  };

  const handleBlankClick = () => {
    if (!blankSelected) {
      onNumberRangeChange({ min: null, max: null });
    }
    onToggleBlank();
  };

  const handleSliderInteractionStart = () => {
    if (sliderDraggingRef) {
      sliderDraggingRef.current = true;
    }
    if (blankSelected) {
      onToggleBlank();
    }
    const currentMin = pendingRange?.min ?? minMaxValues?.min ?? fallbackMin;
    const currentMax = pendingRange?.max ?? minMaxValues?.max ?? fallbackMax;
    onNumberRangeChange({
      min: Math.floor(currentMin),
      max: Math.ceil(currentMax),
    });
  };
  const handleSliderChangeCommitted = () => {
    if (sliderDraggingRef) {
      setTimeout(() => {
        sliderDraggingRef.current = false;
      }, NUMERIC_FILTER_SLIDER_RELEASE_DELAY_MS);
    }
  };

  const handleSliderChange = (_event, value) => {
    const [min, max] = value;
    onNumberRangeChange({
      min,
      max,
    });
  };
  const hasValues = minMaxValues?.min != null && minMaxValues?.max != null;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <DefaultLoader size={FILTER_VALUES_LOADER_SIZE_PX} />
      </Box>
    );
  }

  if (!hasValues && !(blankCount > 0)) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          py: 4,
        }}
      >
        <Typography variant={"body2"} color={"text.secondary"}>
          {translateLabels(labels.NUMBER_FILTER_NO_VALUES, {
            default: "No filter values available",
          })}
        </Typography>
      </Box>
    );
  }

  return (
    <React.Fragment>
      {hasValues && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            px: 1.5,
            py: 1,
            borderBottom: 1,
            borderColor: "divider",
            alignItems: "center",
            justifyContent: "space-around",
          }}
        >
          <PrettyNumberTextField
            size={"small"}
            label={translateLabels(labels.NUMBER_FILTER_MIN, {
              default: "Min",
            })}
            value={pendingMin ?? null}
            prefix={minAffixes.prefix}
            suffix={minAffixes.suffix}
            onChangeNumber={handleMinChange}
            allowClear
            selectOnFocus
            offFormatter={fmt.inputFormatter}
            inputProps={{
              style: {
                width: NUMERIC_FILTER_INPUT_WIDTH,
              },
            }}
          />
          <PrettyNumberTextField
            size={"small"}
            label={translateLabels(labels.NUMBER_FILTER_MAX, {
              default: "Max",
            })}
            value={pendingMax ?? null}
            prefix={maxAffixes.prefix}
            suffix={maxAffixes.suffix}
            onChangeNumber={handleMaxChange}
            allowClear
            selectOnFocus
            offFormatter={fmt.inputFormatter}
            inputProps={{
              style: {
                width: NUMERIC_FILTER_INPUT_WIDTH,
              },
            }}
          />
        </Box>
      )}
      {blankCount > 0 && (
        <MenuItem
          onClick={handleBlankClick}
          selected={blankSelected}
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            borderBottom: 1,
            borderColor: "divider",
            backgroundColor: blankSelected ? "action.selected" : "transparent",
            "&:hover": {
              backgroundColor: blankSelected ? "action.focus" : "action.hover",
            },
          }}
        >
          <Typography
            variant={"body2"}
            sx={{
              fontWeight: blankSelected ? 600 : 400,
              fontSize: "0.875rem",
            }}
          >
            {"<blank>"}
          </Typography>
          <Typography
            variant={"body2"}
            sx={{
              color: "text.secondary",
              flexShrink: 0,
            }}
          >
            {blankCount.toLocaleString()}
          </Typography>
        </MenuItem>
      )}
      {showActions && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            px: 1.5,
            pt: 1,
            pb: 1,
          }}
        >
          <Button
            size={"small"}
            variant={"contained"}
            onClick={onSave}
            fullWidth
            disabled={
              pendingMin != null &&
              pendingMax != null &&
              pendingMax < pendingMin
            }
          >
            {translateLabels(labels.NUMBER_FILTER_SAVE, { default: "Save" })}
          </Button>
          <Button
            size={"small"}
            variant={"contained"}
            color={"secondary"}
            onClick={onCancel}
            fullWidth
          >
            {translateLabels(labels.NUMBER_FILTER_CANCEL, {
              default: "Cancel",
            })}
          </Button>
        </Box>
      )}
      {hasValues && (
        <Box
          sx={{
            px: 4,
            py: 2,
            flex: 1,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography
              variant={"caption"}
              sx={{
                color: "text.secondary",
              }}
            >
              {fmt.display(minMaxValues.min, "")}
            </Typography>
            <Typography
              variant={"caption"}
              sx={{
                color: "text.secondary",
              }}
            >
              {fmt.display(minMaxValues.max, "")}
            </Typography>
          </Box>
          <Box sx={styles.relativeContainer}>
            {histogramBins && histogramBins.length > 0 && maxBinCount > 0 && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 18,
                  left: 0,
                  right: 0,
                  height: graphHeight,
                  display: "flex",
                  alignItems: "flex-end",
                  gap: "1px",
                  pointerEvents: "none",
                }}
              >
                {histogramBins.map((count, index) => {
                  const heightPercent = (count / maxBinCount) * 100;
                  return (
                    <Box
                      key={index}
                      sx={{
                        flex: 1,
                        height: `${heightPercent}%`,
                        backgroundColor: (theme) => theme.palette.primary.main,
                        opacity: 0.2,
                        borderRadius: "2px 2px 0 0",
                        minHeight:
                          count > 0 ? NUMERIC_FILTER_MIN_BAR_HEIGHT_PX : 0,
                      }}
                    />
                  );
                })}
              </Box>
            )}
            <Slider
              value={[sliderValueMin, sliderValueMax]}
              onChange={handleSliderChange}
              onMouseDown={handleSliderInteractionStart}
              onTouchStart={handleSliderInteractionStart}
              onChangeCommitted={handleSliderChangeCommitted}
              valueLabelDisplay={"off"}
              min={fallbackMin}
              max={fallbackMax}
            />
          </Box>
        </Box>
      )}
    </React.Fragment>
  );
};

export const renderNumericFilterChips = (
  numberRange,
  onClearMin,
  onClearMax,
  fmt,
  translateLabels,
  onClearBlank
) => {
  const chips = [];
  const { min, max } = numberRange;
  const minLabel = translateLabels
    ? translateLabels(labels.NUMBER_FILTER_CHIP_MIN, { default: "min" })
    : "min";
  const maxLabel = translateLabels
    ? translateLabels(labels.NUMBER_FILTER_CHIP_MAX, { default: "max" })
    : "max";
  if (numberRange.isEmpty) {
    chips.push(
      <FilterChip
        key={"blank"}
        label={"<blank>"}
        onDelete={onClearBlank}
        sx={{
          ml: 1,
          mr: 0.5,
        }}
      />
    );
  }
  if (min !== undefined && min !== null) {
    chips.push(
      <FilterChip
        key={"min"}
        label={`${minLabel}: ${fmt.short(min)}`}
        onDelete={onClearMin}
        tooltip={fmt.exact(min)}
        sx={{
          ml: chips.length > 0 ? 0.5 : 1,
          mr: 0.5,
        }}
      />
    );
  }
  if (max !== undefined && max !== null) {
    chips.push(
      <FilterChip
        key={"max"}
        label={`${maxLabel}: ${fmt.short(max)}`}
        onDelete={onClearMax}
        tooltip={fmt.exact(max)}
        sx={{
          ml: chips.length > 0 ? 0.5 : 1,
          mr: 1,
        }}
      />
    );
  }
  return chips.length > 0 ? chips : null;
};

export default NumericFilter;
